import { useEffect, useState } from "react";
import {
  DndContext,
  KeyboardSensor,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  SortableContext,
  arrayMove,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { Drawer } from "antd";

import { AppStrings } from "../../../../utils/constants/AppStrings";
import Button from "../../Button/Button";
import ToggleSwitch from "../../ToggleSwitch/ToggleSwitch";
import SortableColumnRow from "./SortableColumnRow";
import styles from "./ConfigurableDataTable.module.css";

function ColumnSettingsDrawer({
  open,
  onClose,
  columns,
  settings,
  onSave,
}) {
  const [draft, setDraft] = useState(settings);
  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  );

  useEffect(() => {
    if (open) {
      setDraft({
        order: [...settings.order],
        visibleKeys: [...settings.visibleKeys],
        widths: { ...settings.widths },
      });
    }
  }, [open, settings]);

  const columnsByKey = new Map(columns.map((column) => [column.key, column]));
  const orderedColumns = draft.order
    .map((key) => columnsByKey.get(key))
    .filter(Boolean);
  const allVisible =
    orderedColumns.length > 0 &&
    orderedColumns.every((column) => draft.visibleKeys.includes(column.key));

  const handleDragEnd = ({ active, over }) => {
    if (!over || active.id === over.id) return;
    const oldIndex = draft.order.indexOf(active.id);
    const newIndex = draft.order.indexOf(over.id);
    if (oldIndex < 0 || newIndex < 0) return;
    setDraft((current) => ({
      ...current,
      order: arrayMove(current.order, oldIndex, newIndex),
    }));
  };

  const setColumnVisibility = (key, visible) => {
    setDraft((current) => ({
      ...current,
      visibleKeys: visible
        ? [...current.visibleKeys, key]
        : current.visibleKeys.filter((visibleKey) => visibleKey !== key),
    }));
  };

  const footer = (
    <div className={styles.drawerFooterActions}>
      <Button
        intent="primary"
        variant="outline"
        size="middle"
        onClick={() => onSave(draft)}
      >
        {AppStrings.SAVE}
      </Button>
    </div>
  );

  return (
    <Drawer
      open={open}
      onClose={onClose}
      title={AppStrings.PAM_CONFIGURABLE_TABLE_COLUMNS_SHOWN}
      placement="right"
      width={400}
      destroyOnHidden
      footer={footer}
      className={styles.drawer}
      classNames={{
        body: styles.drawerBody,
        footer: styles.drawerFooter,
      }}
      data-testid="configurable-table-drawer"
    >
      <div className={styles.selectAllRow}>
        <ToggleSwitch
          value={allVisible}
          onChange={(visible) =>
            setDraft((current) => ({
              ...current,
              visibleKeys: visible
                ? orderedColumns.map((column) => column.key)
                : [],
            }))
          }
          ariaLabel={AppStrings.SELECT_ALL}
        />
        <span>{AppStrings.SELECT_ALL}</span>
      </div>

      <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        onDragEnd={handleDragEnd}
      >
        <SortableContext
          items={orderedColumns.map((column) => column.key)}
          strategy={verticalListSortingStrategy}
        >
          <div className={styles.columnList}>
            {orderedColumns.map((column) => (
              <SortableColumnRow
                key={column.key}
                column={column}
                visible={draft.visibleKeys.includes(column.key)}
                onVisibilityChange={setColumnVisibility}
              />
            ))}
          </div>
        </SortableContext>
      </DndContext>
    </Drawer>
  );
}

export default ColumnSettingsDrawer;
